// ============================================================
// WHAT A TRAIT DRAWS ROUND A DISPLAY FIGURE (plan design-gaps, T1;
// engine/display/traitMarks), on the screen. Under the figure: the plumb line
// and its small ring on the ground, a hoverer's drop lines, a burrower's
// furrow closing behind it. Over it: an incorporeal head's wireframe (an
// inner ring and an equator) and the ripple where it goes into a wall or
// comes out. And the tag under its ring.
// ============================================================

import type { MarkerGeometry, ScreenXY } from './markerGeometry';
import styles from '../../styles/Display.module.css';

/** The trait's marks projected for one frame (BattlefieldTokens `traitMarksFor`). */
export interface ScreenTraitMarks {
  plumb: { from: ScreenXY; to: ScreenXY } | null;
  drops: [ScreenXY, ScreenXY][];
  /** Newest first, each point's age 0 (just now) to 1 (closing). */
  furrow: { at: ScreenXY; age: number }[];
  tag: string | null;
  /** An incorporeal figure: its head drawn as a wireframe. */
  wire: boolean;
  /** It has just gone into a wall or come out of one. */
  ripple: boolean;
}

/** The plumb's ground ring, as a share of the figure's ring (the traits page's R x 0.45). */
const PLUMB_RING = 0.45;

/** The marks under the figure: the plumb and its ground ring, the drop lines, the furrow. */
export function TraitUnder({ id, marks, g }: { id: string; marks: ScreenTraitMarks; g: MarkerGeometry }) {
  const { plumb, drops, furrow } = marks;
  return (
    <g pointerEvents="none" className={styles.traitMarks}>
      {plumb && (
        <g data-testid={`trait-plumb-${id}`}>
          <line className={styles.traitPlumb} x1={plumb.from.x} y1={plumb.from.y} x2={plumb.to.x} y2={plumb.to.y} />
          <ellipse
            className={styles.traitPlumbRing}
            cx={plumb.to.x}
            cy={plumb.to.y}
            rx={g.rx * PLUMB_RING}
            ry={g.ry * PLUMB_RING}
          />
        </g>
      )}
      {drops.map(([top, bottom], i) => (
        <line key={i} data-part="drop" className={styles.traitDrop} x1={top.x} y1={top.y} x2={bottom.x} y2={bottom.y} />
      ))}
      {furrow.slice(1).map((p, i) => (
        <line
          key={i}
          data-part="furrow"
          className={styles.traitFurrow}
          x1={furrow[i].at.x}
          y1={furrow[i].at.y}
          x2={p.at.x}
          y2={p.at.y}
          strokeWidth={3 * (1 - p.age)}
          opacity={0.35 * (1 - p.age)}
        />
      ))}
    </g>
  );
}

/** The marks over the figure: an incorporeal head's wireframe and its ripple at a wall. */
export function TraitOver({ marks, body, g }: { marks: ScreenTraitMarks; body: ScreenXY; g: MarkerGeometry }) {
  if (!marks.wire) return null;
  const r = g.bodyR;
  return (
    <g pointerEvents="none" className={styles.traitWire}>
      <circle cx={body.x} cy={body.y} r={r * 0.5} />
      <ellipse cx={body.x} cy={body.y} rx={r} ry={r * 0.35} />
      {marks.ripple && (
        <ellipse
          data-testid="trait-ripple"
          className={styles.traitRipple}
          cx={body.x}
          cy={body.y}
          rx={r}
          ry={r * 1.4}
        />
      )}
    </g>
  );
}

/** The tag under the figure's ring: what it is doing, how high, a size not a man's. */
export function TraitTag({ id, tag, foot, g }: { id: string; tag: string; foot: ScreenXY; g: MarkerGeometry }) {
  return (
    <text
      data-testid={`token-tag-${id}`}
      className={styles.traitTag}
      x={foot.x}
      y={foot.y + g.ry + 12}
      textAnchor="middle"
    >
      {tag}
    </text>
  );
}
